/**
 * Pompeii — Product Info Custom Element
 * Handles: variant selection, price updates, quantity, share, popups
 * Registered as <product-info>
 */
class ProductInfo extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.section;
    this.productUrl = this.dataset.productUrl;
    this.form = this.querySelector('form[data-type="add-to-cart-form"]') || this.querySelector('.pompeii-pdp__form') || this.querySelector('.product-form');
    this.variantInput = this.form?.querySelector('input[name="id"]');
    this.variants = this._loadVariants();

    // Cache DOM references
    this.priceEl = this.querySelector('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.submitButton = this.querySelector('.product-form__submit');
    this.isMarketRestricted = this.dataset.marketRestricted === 'true';
    this.klaviyoInline = this.querySelector('[data-klaviyo-inline]');
    this.klaviyoEmail = this.querySelector('[data-klaviyo-email]');
    this.klaviyoSubmit = this.querySelector('[data-klaviyo-submit]');
    this.klaviyoMessage = this.querySelector('[data-klaviyo-message]');
    this.klaviyoVariantId = this.querySelector('[data-klaviyo-variant-id]');
    this.klaviyoCompanyId = this.querySelector('[data-klaviyo-company-id]');
    this.klaviyoMarketing = this.querySelector('[data-klaviyo-marketing]');
    this.priceBadge = this.querySelector('.price__badge--sale');
  }

  connectedCallback() {
    this._initVariantPicker();
    this._initQuantitySelectors();
    this._initShareButtons();
    this._initPopups();
    this._initFormSubmit();
    this._initBackInStock();
    this._initStoreAvailability();
    this._syncInitialVariantState();
    this._initMobilePurchaseSticky();
    this._initDesktopStickyAlignment();
    this._initInfiniteGallery();
  }

  disconnectedCallback() {
    if (this._onStickyResize) {
      window.removeEventListener('resize', this._onStickyResize);
    }
    if (this._stickyResizeObserver) {
      this._stickyResizeObserver.disconnect();
    }
    if (this._stickyRaf) {
      cancelAnimationFrame(this._stickyRaf);
    }
    if (this._onMobileStickyScroll) {
      window.removeEventListener('scroll', this._onMobileStickyScroll);
    }
    if (this._onMobileStickyResize) {
      window.removeEventListener('resize', this._onMobileStickyResize);
      window.removeEventListener('orientationchange', this._onMobileStickyResize);
      window.removeEventListener('load', this._onMobileStickyResize);
    }
    if (this._onVisualViewportChange && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._onVisualViewportChange);
      window.visualViewport.removeEventListener('scroll', this._onVisualViewportChange);
    }
    if (this._mobileStickyRaf) {
      cancelAnimationFrame(this._mobileStickyRaf);
    }
    if (this._mobileStickyResizeObserver) {
      this._mobileStickyResizeObserver.disconnect();
    }
    if (this.mobilePurchaseSpacer?.parentNode) {
      this.mobilePurchaseSpacer.parentNode.removeChild(this.mobilePurchaseSpacer);
    }
    if (this._galleryLoopTimeout) {
      clearTimeout(this._galleryLoopTimeout);
    }
    if (this._gallery && this._onGalleryTouchStart) {
      this._gallery.removeEventListener('touchstart', this._onGalleryTouchStart);
    }
    if (this._gallery && this._onGalleryTouchMove) {
      this._gallery.removeEventListener('touchmove', this._onGalleryTouchMove);
    }
    if (this._gallery && this._onGalleryTouchEnd) {
      this._gallery.removeEventListener('touchend', this._onGalleryTouchEnd);
    }
  }

  /* ─── Variant Data ─── */

  _loadVariants() {
    const el = document.getElementById(`ProductVariants-${this.sectionId}`);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('[ProductInfo] Failed to parse variants', e);
      return [];
    }
  }

  /* ─── Variant Picker ─── */

  _initVariantPicker() {
    // Radio buttons
    this.querySelectorAll('.variant-radio').forEach(radio => {
      radio.addEventListener('change', () => this._onVariantChange());
    });

    // Select dropdowns
    this.querySelectorAll('.variant-select').forEach(select => {
      select.addEventListener('change', () => this._onVariantChange());
    });
  }

  _onVariantChange() {
    const selectedOptions = this._getSelectedOptions();
    const variant = this._findVariant(selectedOptions);

    if (!variant) return;

    // Update hidden input
    if (this.variantInput) {
      this.variantInput.value = variant.id;
    }

    this._updatePrice(variant);
    this._updateButton(variant);
    this._updateURL(variant);
    this._updateColorLabel();
    this._updateStoreAvailability(variant);

    // Dispatch custom event for other components
    this.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: { variant },
        bubbles: true,
      })
    );
  }

  _syncInitialVariantState() {
    let variant = null;

    if (this.variantInput?.value) {
      variant = this.variants.find(v => String(v.id) === String(this.variantInput.value));
    }

    if (!variant) {
      const selectedOptions = this._getSelectedOptions();
      variant = this._findVariant(selectedOptions);
    }

    if (!variant) return;

    this._updatePrice(variant);
    this._updateButton(variant);
    this._updateColorLabel();
    this._updateStoreAvailability(variant);
  }

  _getSelectedOptions() {
    const options = [];
    this.querySelectorAll('.variant-fieldset').forEach(fieldset => {
      const checked = fieldset.querySelector('.variant-radio:checked');
      const select = fieldset.querySelector('.variant-select');
      if (checked) {
        options.push(checked.value);
      } else if (select) {
        options.push(select.value);
      }
    });
    return options;
  }

  _findVariant(options) {
    return this.variants.find(v =>
      v.options.every((opt, i) => opt === options[i])
    );
  }

  /* ─── Price Update ─── */

  _updatePrice(variant) {
    if (this.priceEl) {
      this.priceEl.textContent = this._formatMoney(variant.price);

      // Toggle sale class
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.priceEl.classList.add('price__current--on-sale');
      } else {
        this.priceEl.classList.remove('price__current--on-sale');
      }
    }

    if (this.comparePriceEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.comparePriceEl.style.display = '';
        const s = this.comparePriceEl.querySelector('s');
        if (s) s.textContent = this._formatMoney(variant.compare_at_price);
      } else {
        this.comparePriceEl.style.display = 'none';
      }
    }

    if (this.priceBadge) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        this.priceBadge.textContent = `-${pct}%`;
        this.priceBadge.style.display = '';
      } else {
        this.priceBadge.style.display = 'none';
      }
    }
  }

  /* ─── Button Update ─── */

  _updateButton(variant) {
    if (!this.submitButton) return;
    const textEl = this.submitButton.querySelector('.button__text');

    if (this.isMarketRestricted) {
      this.submitButton.type = 'button';
      this.submitButton.hidden = true;
      this.submitButton.style.display = 'none';
      this.submitButton.setAttribute('aria-hidden', 'true');
      if (this.klaviyoInline) {
        this.klaviyoInline.hidden = true;
      }
      return;
    }

    if (variant.available) {
      this.submitButton.type = 'submit';
      this.submitButton.hidden = false;
      this.submitButton.style.display = '';
      this.submitButton.setAttribute('aria-hidden', 'false');
      if (this.klaviyoInline) {
        this.klaviyoInline.hidden = true;
      }
    } else {
      this.submitButton.type = 'submit';
      this.submitButton.hidden = true;
      this.submitButton.style.display = 'none';
      this.submitButton.setAttribute('aria-hidden', 'true');
      if (this.klaviyoInline) {
        this.klaviyoInline.hidden = false;
      }
    }

    if (this.klaviyoVariantId) {
      this.klaviyoVariantId.dataset.value = String(variant.id);
    }

    this._resetBackInStockState();

    const addButtonText = variant.preorder
      ? this.submitButton.dataset.preorderText || 'PREORDER'
      : this.submitButton.dataset.defaultText || 'AÑADIR A LA CESTA';

    this.submitButton.dataset.addText = addButtonText;
    this.submitButton.setAttribute('aria-label', addButtonText);

    if (textEl) {
      textEl.textContent = addButtonText;
    }
  }

  _initBackInStock() {
    if (!this.klaviyoInline || !this.klaviyoSubmit) return;

    this.klaviyoSubmit.addEventListener('click', () => {
      this._submitBackInStock();
    });

    this.klaviyoEmail?.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this._submitBackInStock();
      }
    });
  }

  _resetBackInStockState() {
    if (!this.klaviyoInline) return;

    if (this.klaviyoMessage) {
      this.klaviyoMessage.textContent = '';
      this.klaviyoMessage.classList.remove('is-visible', 'is-success', 'is-error');
    }

    if (this.klaviyoEmail) {
      this.klaviyoEmail.disabled = false;
    }

    if (this.klaviyoSubmit) {
      this.klaviyoSubmit.disabled = false;
      const textEl = this.klaviyoSubmit.querySelector('.button__text');
      if (textEl) {
        textEl.textContent = this.klaviyoSubmit.dataset.defaultText || 'SUBMIT';
      }
      this.klaviyoSubmit.removeAttribute('aria-busy');
    }
  }

  _setBackInStockMessage(type, text) {
    if (!this.klaviyoMessage) return;

    this.klaviyoMessage.textContent = text;
    this.klaviyoMessage.classList.add('is-visible');
    this.klaviyoMessage.classList.toggle('is-success', type === 'success');
    this.klaviyoMessage.classList.toggle('is-error', type === 'error');
  }

  async _submitBackInStock() {
    const email = this.klaviyoEmail?.value?.trim();
    const companyId = this.klaviyoCompanyId?.dataset.value;
    const variantId = this.klaviyoVariantId?.dataset.value || this.variantInput?.value;

    if (!email || !this.klaviyoEmail?.checkValidity()) {
      this._setBackInStockMessage('error', 'Introduce un email válido.');
      this.klaviyoEmail?.focus();
      return;
    }

    if (!companyId || !variantId) {
      this._setBackInStockMessage('error', 'No se ha podido preparar la suscripción.');
      return;
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    if (this.klaviyoSubmit) {
      this.klaviyoSubmit.disabled = true;
      const textEl = this.klaviyoSubmit.querySelector('.button__text');
      if (textEl) {
        textEl.textContent = this.klaviyoSubmit.dataset.loadingText || 'ENVIANDO...';
      }
      this.klaviyoSubmit.setAttribute('aria-busy', 'true');
    }
    if (this.klaviyoEmail) {
      this.klaviyoEmail.disabled = true;
    }
    if (this.klaviyoMessage) {
      this.klaviyoMessage.textContent = '';
      this.klaviyoMessage.classList.remove('is-visible', 'is-success', 'is-error');
    }

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            revision: '2024-06-15',
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.status === 202) {
        this._setBackInStockMessage('success', 'Te avisaremos cuando vuelva a estar disponible.');
        if (this.klaviyoEmail) {
          this.klaviyoEmail.value = '';
          this.klaviyoEmail.disabled = true;
        }
        if (this.klaviyoSubmit) {
          this.klaviyoSubmit.disabled = true;
          this.klaviyoSubmit.removeAttribute('aria-busy');
          const textEl = this.klaviyoSubmit.querySelector('.button__text');
          if (textEl) {
            textEl.textContent = this.klaviyoSubmit.dataset.defaultText || 'SUBMIT';
          }
        }
        return;
      }

      let errorPayload = null;
      try {
        errorPayload = await response.json();
      } catch (error) {
        errorPayload = null;
      }

      if (errorPayload) {
        console.warn('[ProductInfo] Klaviyo back-in-stock error', response.status, errorPayload);
      }

      this._setBackInStockMessage('error', 'No se ha podido registrar la solicitud. Revisa el email e inténtalo de nuevo.');
    } catch (error) {
      console.warn('[ProductInfo] Klaviyo back-in-stock request failed', error);
      this._setBackInStockMessage('error', 'No se ha podido registrar la solicitud. Revisa el email e inténtalo de nuevo.');
    } finally {
      if (this.klaviyoMessage?.classList.contains('is-success')) return;

      if (this.klaviyoSubmit) {
        this.klaviyoSubmit.disabled = false;
        this.klaviyoSubmit.removeAttribute('aria-busy');
        const textEl = this.klaviyoSubmit.querySelector('.button__text');
        if (textEl) {
          textEl.textContent = this.klaviyoSubmit.dataset.defaultText || 'SUBMIT';
        }
      }
      if (this.klaviyoEmail) {
        this.klaviyoEmail.disabled = false;
      }
    }
  }

  /* ─── URL Update ─── */

  _updateURL(variant) {
    if (!variant || !window.history.replaceState) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  /* ─── Color Label Update ─── */

  _updateColorLabel() {
    this.querySelectorAll('.variant-fieldset').forEach(fieldset => {
      const label = fieldset.querySelector('[data-option-value]');
      const checked = fieldset.querySelector('.variant-radio:checked');
      if (label && checked) {
        label.textContent = checked.value;
      }
    });
  }

  /* ─── Quantity Selector ─── */

  _initQuantitySelectors() {
    this.querySelectorAll('.quantity-selector').forEach(selector => {
      const input = selector.querySelector('.quantity-input');
      const minus = selector.querySelector('.quantity-minus');
      const plus = selector.querySelector('.quantity-plus');

      if (!input) return;

      minus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || 1;
        input.value = Math.max(1, val - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      plus?.addEventListener('click', () => {
        const val = parseInt(input.value, 10) || 1;
        input.value = val + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      input.addEventListener('change', () => {
        if (parseInt(input.value, 10) < 1 || isNaN(parseInt(input.value, 10))) {
          input.value = 1;
        }
      });
    });
  }

  /* ─── Share Buttons ─── */

  _initShareButtons() {
    this.querySelectorAll('.share-copy-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(window.location.href);
          const original = btn.querySelector('.whatsapp-button__text')?.textContent
            || btn.getAttribute('data-label')
            || 'Copiar enlace';
          btn.classList.add('share-copy-btn--copied');
          const textEl = btn.querySelector('svg');
          if (textEl && textEl.nextSibling) {
            textEl.nextSibling.textContent = ' ✓ Copiado';
          }
          setTimeout(() => {
            btn.classList.remove('share-copy-btn--copied');
            if (textEl && textEl.nextSibling) {
              textEl.nextSibling.textContent = '';
            }
          }, 2000);
        } catch (e) {
          // Fallback for older browsers
          const textarea = document.createElement('textarea');
          textarea.value = window.location.href;
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }
      });
    });
  }

  /* ─── Popup / Modal ─── */

  _initPopups() {
    this.querySelectorAll('.popup-trigger').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = trigger.getAttribute('aria-controls');
        const dialog = document.getElementById(targetId);
        if (dialog && dialog.showModal) {
          dialog.showModal();
          document.body.style.overflow = 'hidden';
        }
      });
    });

    this.querySelectorAll('.popup-dialog__close').forEach(btn => {
      btn.addEventListener('click', () => {
        const dialog = btn.closest('dialog');
        if (dialog) {
          dialog.close();
          document.body.style.overflow = '';
        }
      });
    });

    // Close on backdrop click
    this.querySelectorAll('.popup-dialog').forEach(dialog => {
      dialog.addEventListener('click', (e) => {
        if (e.target === dialog) {
          dialog.close();
          document.body.style.overflow = '';
        }
      });
    });
  }

  /* ─── Store Availability Toggle ─── */

  _initStoreAvailability() {
    this.querySelectorAll('.store-availability__trigger').forEach(trigger => {
      trigger.addEventListener('click', () => {
        const list = trigger.closest('.store-availability')?.querySelector('.store-availability__list');
        if (!list) return;
        const isHidden = list.hasAttribute('hidden');
        if (isHidden) {
          list.removeAttribute('hidden');
          trigger.setAttribute('aria-expanded', 'true');
        } else {
          list.setAttribute('hidden', '');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  _updateStoreAvailability(variant) {
    const storeAvailability = this.querySelector('.store-availability');
    if (!storeAvailability) return;

    const list = storeAvailability.querySelector('.store-availability__list');
    if (!list) return;

    const inventory = variant?.store_inventory || {};
    const madridUnits = Number(inventory.madrid) || 0;
    const sevillaUnits = Number(inventory.sevilla) || 0;
    const items = [];

    if (madridUnits > 0) {
      items.push(this._renderStoreAvailabilityItem('Madrid', madridUnits));
    }

    if (sevillaUnits > 0) {
      items.push(this._renderStoreAvailabilityItem('Sevilla', sevillaUnits));
    }

    list.innerHTML = items.length
      ? items.join('')
      : '<p class="store-availability__empty">No hay disponibilidad en tiendas físicas.</p>';
  }

  _renderStoreAvailabilityItem(storeName, units) {
    const unitLabel = units === 1 ? 'unidad' : 'unidades';

    return `<p class="store-availability__sentence">${units} ${unitLabel} en nuestra tienda de ${storeName}.</p>`;
  }

  /* ─── Form Submit (AJAX add to cart) ─── */

  /* ─── Infinite Gallery (mobile swipe) ─── */

  _initInfiniteGallery() {
    if (window.innerWidth > 749) return;
    const gallery = this.querySelector('.product-gallery');
    if (!gallery) return;
    if (gallery.dataset.infiniteGalleryInit === 'true') return;
    this._gallery = gallery;
    const items = gallery.querySelectorAll('.product-gallery__item');
    if (items.length < 2) return;
    gallery.dataset.infiniteGalleryInit = 'true';
    this._galleryRealItemCount = items.length;

    // Clone first and last slides
    const firstClone = items[0].cloneNode(true);
    const lastClone = items[items.length - 1].cloneNode(true);
    firstClone.classList.add('product-gallery__item--clone');
    lastClone.classList.add('product-gallery__item--clone');
    gallery.appendChild(firstClone);
    gallery.insertBefore(lastClone, items[0]);

    // Scroll to real first item (skip the prepended clone)
    requestAnimationFrame(() => {
      this._setGalleryIndex(1, false);
    });

    this._galleryTouchStartX = 0;
    this._galleryTouchStartY = 0;
    this._galleryTouchDeltaX = 0;
    this._galleryTouchDeltaY = 0;

    this._onGalleryTouchStart = (event) => {
      const touch = event.touches ? event.touches[0] : event;
      this._galleryTouchStartX = touch.clientX;
      this._galleryTouchStartY = touch.clientY;
      this._galleryTouchDeltaX = 0;
      this._galleryTouchDeltaY = 0;
    };

    this._onGalleryTouchMove = (event) => {
      const touch = event.touches ? event.touches[0] : event;
      this._galleryTouchDeltaX = touch.clientX - this._galleryTouchStartX;
      this._galleryTouchDeltaY = touch.clientY - this._galleryTouchStartY;

      if (Math.abs(this._galleryTouchDeltaX) > Math.abs(this._galleryTouchDeltaY)) {
        event.preventDefault();
      }
    };

    this._onGalleryTouchEnd = () => {
      const absDeltaX = Math.abs(this._galleryTouchDeltaX);
      const absDeltaY = Math.abs(this._galleryTouchDeltaY);

      if (absDeltaX < 24 || absDeltaX <= absDeltaY) return;

      const direction = this._galleryTouchDeltaX < 0 ? 1 : -1;
      this._moveGalleryBy(direction);
    };

    gallery.addEventListener('touchstart', this._onGalleryTouchStart, { passive: true });
    gallery.addEventListener('touchmove', this._onGalleryTouchMove, { passive: false });
    gallery.addEventListener('touchend', this._onGalleryTouchEnd, { passive: true });
  }

  _getGalleryStepSize(gallery, item) {
    if (!gallery || !item) return 0;
    const galleryStyles = window.getComputedStyle(gallery);
    const gap = parseFloat(galleryStyles.columnGap || galleryStyles.gap || '0') || 0;
    return item.getBoundingClientRect().width + gap;
  }

  _getGalleryNearestIndex(gallery, item) {
    const step = this._getGalleryStepSize(gallery, item);
    if (!step) return 0;
    return Math.round(gallery.scrollLeft / step);
  }

  _setGalleryIndex(index, smooth = true) {
    if (!this._gallery) return;
    const item = this._gallery.querySelector('.product-gallery__item');
    if (!item) return;

    const step = this._getGalleryStepSize(this._gallery, item);
    if (!step) return;

    this._gallery.scrollTo({
      left: index * step,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  _moveGalleryBy(delta) {
    if (!this._gallery || !this._galleryRealItemCount) return;

    const item = this._gallery.querySelector('.product-gallery__item');
    if (!item) return;

    const currentIndex = this._getGalleryNearestIndex(this._gallery, item);
    const targetIndex = currentIndex + delta;

    this._setGalleryIndex(targetIndex, true);

    clearTimeout(this._galleryLoopTimeout);
    this._galleryLoopTimeout = window.setTimeout(() => {
      if (targetIndex <= 0) {
        this._setGalleryIndex(this._galleryRealItemCount, false);
      } else if (targetIndex >= this._galleryRealItemCount + 1) {
        this._setGalleryIndex(1, false);
      }
    }, 260);
  }

  /* Sticky side columns alignment (desktop) */

  _initMobilePurchaseSticky() {
    this.mobilePurchase = this.querySelector('.pompeii-pdp__purchase');
    this.mobilePurchaseInner = this.querySelector('.pompeii-pdp__purchase-inner');
    this.mobileGallery = this.querySelector('.pompeii-pdp__gallery');
    this.mobileFooter = document.querySelector('.pompeii-ft') || document.querySelector('footer[role="contentinfo"]');

    if (!this.mobilePurchase || !this.mobilePurchase.parentNode) return;

    this.mobilePurchaseSpacer = document.createElement('div');
    this.mobilePurchaseSpacer.className = 'pompeii-pdp__purchase-spacer';
    this.mobilePurchase.parentNode.insertBefore(this.mobilePurchaseSpacer, this.mobilePurchase);

    this._onMobileStickyScroll = () => {
      if (this._mobileStickyRaf) cancelAnimationFrame(this._mobileStickyRaf);
      this._mobileStickyRaf = requestAnimationFrame(() => this._updateMobilePurchaseSticky());
    };

    this._onMobileStickyResize = () => {
      this._measureMobilePurchaseBaseMetrics(window.scrollY <= 4);
      this._onMobileStickyScroll();
    };

    this._onVisualViewportChange = () => {
      this._updateMobileViewportOffset();
      this._onMobileStickyScroll();
    };

    window.addEventListener('scroll', this._onMobileStickyScroll, { passive: true });
    window.addEventListener('resize', this._onMobileStickyResize, { passive: true });
    window.addEventListener('orientationchange', this._onMobileStickyResize, { passive: true });
    window.addEventListener('load', this._onMobileStickyResize, { passive: true, once: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._onVisualViewportChange, { passive: true });
      window.visualViewport.addEventListener('scroll', this._onVisualViewportChange, { passive: true });
    }

    if ('ResizeObserver' in window && this.mobilePurchaseInner) {
      this._mobileStickyResizeObserver = new ResizeObserver(() => {
        this._measureMobilePurchaseBaseMetrics(window.scrollY <= 4);
        this._onMobileStickyScroll();
      });
      this._mobileStickyResizeObserver.observe(this.mobilePurchaseInner);
      if (this.mobileGallery) {
        this._mobileStickyResizeObserver.observe(this.mobileGallery);
      }
    }

    this._updateMobileViewportOffset();
    this._measureMobilePurchaseBaseMetrics(true);
    this._updateMobilePurchaseSticky();
  }

  _updateMobileViewportOffset() {
    if (!this.mobilePurchase) return;

    let bottomOffset = 0;
    if (window.visualViewport) {
      bottomOffset = Math.max(
        0,
        Math.round(window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop)
      );
    }

    this.mobilePurchase.style.setProperty('--pdp-mobile-viewport-offset', `${bottomOffset}px`);
  }

  _measureMobilePurchaseBaseMetrics(updateStickyTop = false) {
    if (!this.mobilePurchase) return;
    const wasSticky = this.mobilePurchase.classList.contains('pompeii-pdp__purchase--mobile-sticky');
    if (wasSticky) {
      this.mobilePurchase.classList.remove('pompeii-pdp__purchase--mobile-sticky');
    }
    const rect = this.mobilePurchase.getBoundingClientRect();
    this.mobilePurchaseBaseHeight = rect.height;
    if (updateStickyTop || typeof this.mobilePurchaseStickyTop !== 'number') {
      this.mobilePurchaseStickyTop = Math.max(0, Math.round(rect.top));
      this.mobilePurchase.style.setProperty('--pdp-mobile-sticky-top', `${this.mobilePurchaseStickyTop}px`);
    }
    if (wasSticky) {
      this.mobilePurchase.classList.add('pompeii-pdp__purchase--mobile-sticky');
    }
  }

  _updateMobilePurchaseSticky() {
    if (!this.mobilePurchase || !this.mobilePurchaseSpacer) return;

    const isMobile = window.innerWidth <= 749;
    if (!isMobile) {
      this.mobilePurchase.style.setProperty('--pdp-mobile-viewport-offset', '0px');
      this._deactivateMobilePurchaseSticky();
      return;
    }

    this._updateMobileViewportOffset();

    const isCurrentlySticky = this.mobilePurchase.classList.contains('pompeii-pdp__purchase--mobile-sticky');
    const spacerTop = this.mobilePurchaseSpacer.getBoundingClientRect().top;
    const stickyTop = typeof this.mobilePurchaseStickyTop === 'number' ? this.mobilePurchaseStickyTop : 0;

    // Hysteresis: use different thresholds for activate vs deactivate to prevent flicker
    const activateThreshold = stickyTop;
    const deactivateThreshold = stickyTop + 50;
    const shouldStick = window.scrollY > 0 && spacerTop <= (isCurrentlySticky ? deactivateThreshold : activateThreshold);

    // Sticky bar stays visible all the way down; footer overlap is
    // handled by _updateMobilePurchaseFooterOffset.

    if (!shouldStick) {
      this._deactivateMobilePurchaseSticky();
      return;
    }

    this._activateMobilePurchaseSticky();
    this._syncMobilePurchaseOffset();
    this._updateMobilePurchaseFooterOffset();
  }

  _activateMobilePurchaseSticky() {
    if (!this.mobilePurchase || !this.mobilePurchaseSpacer) return;

    const reserveHeight = this.mobilePurchaseBaseHeight || this.mobilePurchase.getBoundingClientRect().height;
    this.mobilePurchaseSpacer.style.height = `${Math.ceil(reserveHeight)}px`;
    this.mobilePurchaseSpacer.classList.add('is-active');
    this.mobilePurchase.classList.add('pompeii-pdp__purchase--mobile-sticky');
  }

  _deactivateMobilePurchaseSticky() {
    if (!this.mobilePurchase || !this.mobilePurchaseSpacer) return;

    this.mobilePurchase.classList.remove('pompeii-pdp__purchase--mobile-sticky');
    this.mobilePurchase.style.removeProperty('--pdp-mobile-sticky-translate-y');
    this.mobilePurchaseSpacer.classList.remove('is-active');
    this.mobilePurchaseSpacer.style.height = '0px';
  }


  _syncMobilePurchaseOffset() {
    if (!this.mobilePurchase || !this.mobilePurchaseSpacer) return;

    const stickyHeight = this.mobilePurchase.getBoundingClientRect().height;
    this.mobilePurchaseSpacer.style.height = `${Math.ceil(stickyHeight)}px`;
  }

  _updateMobilePurchaseFooterOffset() {
    if (!this.mobilePurchase) return;
    // Sticky CTA stays pinned to the viewport bottom on mobile, always.
    this.mobilePurchase.style.setProperty('--pdp-mobile-sticky-translate-y', '0px');
  }

  _initDesktopStickyAlignment() {
    this.pdpGrid = this.querySelector('.pompeii-pdp');
    this.purchaseColumn = this.querySelector('.pompeii-pdp__purchase');
    this.detailsColumn = this.querySelector('.pompeii-pdp__details');
    this.purchaseInner = this.querySelector('.pompeii-pdp__purchase-inner');
    this.detailsInner = this.querySelector('.pompeii-pdp__details-inner');
    this.galleryColumn = this.querySelector('.pompeii-pdp__gallery');

    if (!this.pdpGrid || !this.purchaseColumn || !this.detailsColumn || !this.purchaseInner || !this.detailsInner) {
      return;
    }

    this._onStickyResize = () => {
      if (this._stickyRaf) cancelAnimationFrame(this._stickyRaf);
      this._stickyRaf = requestAnimationFrame(() => this._updateDesktopStickyAlignment());
    };

    window.addEventListener('resize', this._onStickyResize, { passive: true });

    if ('ResizeObserver' in window) {
      this._stickyResizeObserver = new ResizeObserver(() => this._onStickyResize());
      this._stickyResizeObserver.observe(this.purchaseInner);
      this._stickyResizeObserver.observe(this.detailsInner);
      this._stickyResizeObserver.observe(this.pdpGrid);
      this.querySelectorAll('.size-selector').forEach((selector) => {
        this._stickyResizeObserver.observe(selector);
      });
    }

    this._onStickyResize();
    this._updateMobilePurchaseSticky();
  }

  _updateDesktopStickyAlignment() {
    if (!this.purchaseColumn || !this.detailsColumn || !this.purchaseInner || !this.detailsInner) return;

    // Desktop only: keep tablet/mobile untouched.
    if (window.innerWidth < 1025) {
      this.purchaseColumn.style.removeProperty('--pdp-sticky-top');
      this.detailsColumn.style.removeProperty('--pdp-sticky-top');
      this._updateDesktopSizeSelectorLayout(false);
      return;
    }

    const minTop = this._getDesktopStickyMinTop();
    const purchaseTop = this._getCenteredTop(this.purchaseInner, minTop);
    const detailsTop = this._getCenteredTop(this.detailsInner, minTop);
    // Keep both side blocks visually aligned (same vertical anchor).
    const sharedTop = Math.max(purchaseTop, detailsTop);

    this._setStickyTop(this.purchaseColumn, sharedTop);
    this._setStickyTop(this.detailsColumn, sharedTop);
    this._updateDesktopSizeSelectorLayout(true);
  }

  _getDesktopStickyMinTop() {
    // True viewport centering: no compensation from gallery/header paddings.
    return 0;
  }

  _getCenteredTop(inner, minTop) {
    const innerHeight = this._getStickyInnerHeight(inner);
    return Math.max(minTop, (window.innerHeight - innerHeight) / 2);
  }

  _getStickyInnerHeight(inner) {
    const totalHeight = inner.getBoundingClientRect().height;
    const expandedStoreLists = inner.querySelectorAll('.store-availability__list:not([hidden])');

    if (!expandedStoreLists.length) return totalHeight;

    let expandedHeight = 0;
    expandedStoreLists.forEach((list) => {
      const styles = window.getComputedStyle(list);
      const marginTop = parseFloat(styles.marginTop) || 0;
      const marginBottom = parseFloat(styles.marginBottom) || 0;
      expandedHeight += list.getBoundingClientRect().height + marginTop + marginBottom;
    });

    return Math.max(0, totalHeight - expandedHeight);
  }

  _setStickyTop(column, top) {
    column.style.setProperty('--pdp-sticky-top', `${Math.round(top)}px`);
  }

  _updateDesktopSizeSelectorLayout(isDesktop = window.innerWidth >= 1025) {
    this.querySelectorAll('.size-selector').forEach((selector) => {
      if (!isDesktop) {
        selector.classList.remove('size-selector--wrapped');
        return;
      }

      const sizeGuide = selector.querySelector('.size-guide-link');
      const firstSize = selector.querySelector('.size-option');
      if (!sizeGuide || !firstSize) return;

      // Measure natural layout first; otherwise wrapped state can become sticky.
      selector.classList.remove('size-selector--wrapped');
      // Force reflow so offsetTop reflects the class removal immediately.
      void selector.offsetWidth;

      const guideTop = sizeGuide.offsetTop;
      const sizeTop = firstSize.offsetTop;
      const wrapped = Math.abs(guideTop - sizeTop) > 1;
      selector.classList.toggle('size-selector--wrapped', wrapped);
    });
  }

  _initFormSubmit() {
    if (!this.form) return;
    this.form.addEventListener('submit', (e) => this._onFormSubmit(e));
  }

  _onFormSubmit(event) {
    event.preventDefault();
    if (this.isMarketRestricted) return;
    if (!this.submitButton || this.submitButton.disabled) return;

    // Loading state
    this.submitButton.classList.add('is-loading');
    this.submitButton.setAttribute('aria-busy', 'true');

    const formData = new FormData(this.form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/javascript' },
      body: formData
    })
      .then(r => r.json())
      .then(data => {
        const textEl = this.submitButton.querySelector('.button__text');
        if (textEl) {
          const original = textEl.textContent;
          textEl.textContent = '\u2713 AÑADIDO';
          setTimeout(() => { textEl.textContent = original; }, 2000);
        }
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: data } }));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(() => {
        const textEl = this.submitButton.querySelector('.button__text');
        if (textEl) {
          const original = textEl.textContent;
          textEl.textContent = 'ERROR';
          setTimeout(() => { textEl.textContent = original; }, 2000);
        }
      })
      .finally(() => {
        this.submitButton.classList.remove('is-loading');
        this.submitButton.setAttribute('aria-busy', 'false');
      });
  }

  /* ─── Helpers ─── */

  _formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: Shopify?.currency?.active || 'EUR',
      minimumFractionDigits: 2,
    }).format(cents / 100);
  }
}

/* Register custom element */
if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}
